"use client";

import React, { type ReactNode } from "react";

import { DOMNode, Element as DomElement } from "html-react-parser";

import {
  renderCaption,
  renderChart,
  renderEquation,
  renderFigure,
  renderStyledElement,
  isRawTag,
  renderRawTag,
  renderInlineMathElement,
} from "./renderers";
import type { Element } from "./utils/type";

/**
 * HTML DOM 노드를 React 요소로 변환하는 transform 함수
 */
export function createHtmlTransform(elementData: Element, index: number) {
  const transform = (
    reactNode: ReactNode,
    domNode: DOMNode,
    nodeIndex: number,
  ): React.ReactElement | null | undefined => {
    const { category, content, base64_encoding } = elementData;
    const html = content?.html || "";

    const element = domNode as DomElement;

    // 텍스트 노드 등 tag가 아닌 경우에는 기본 렌더링을 유지
    if ((element as any).type !== "tag") {
      return reactNode as React.ReactElement;
    }

    // 인라인 수식: 파싱 전에 wrapInlineMathInHtml이 감싼 <imath> element를 MathJax로 렌더
    if (element.name === "imath") {
      return renderInlineMathElement(element);
    }

    // 특수 케이스 처리: equation category (domNode 타입과 관계없이 처리)
    if (category === "equation") {
      return renderEquation(html);
    }

    // 특수 케이스 처리: chart category
    if (category === "chart") {
      return renderChart(html, elementData, index, createHtmlTransform);
    }

    // 폼/스크립트/임베드 계열 태그들은 실제 동작 대신 태그 문자열로만 렌더링
    if (isRawTag(element)) {
      return renderRawTag(element);
    }

    // 특수 태그 처리: caption
    if (element.name === "caption") {
      return renderCaption(element);
    }

    // 특수 태그 처리: figure
    if (element.name === "figure") {
      return renderFigure(element, base64_encoding || "");
    }
    // 일반 태그 스타일 적용
    return renderStyledElement(element, nodeIndex, transform);
  };

  return transform;
}
